import React, {
  ChangeEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  callingCodeToCountryCodeMap,
  countriesArraySortedByName,
  getCountryCallingCode,
  getCountryFlag,
  Country,
} from "country-explorer";

import { Input, InputProps } from "./Input";
import { InputSelect } from "./InputSelect";
import {
  StyledFlagTriggerLabel,
  StyledOptionCode,
  StyledOptionFlag,
  StyledOptionName,
  StyledOptionRowEnd,
  StyledOptionWrapper,
} from "./Styles";
import { useForkRef } from "../../utils";
import { RadioButton } from "../RadioButton";

export interface InputPhoneProps
  extends Omit<
    InputProps,
    "type" | "startAdornment" | "onChange" | "value" | "size"
  > {
  /** Input value */
  value?: string;
  /** Default country code to be selected */
  defaultCountry?: string;
  /** onChange function */
  onChange?: (val: string) => void;
  /** Input size */
  size?: "small" | "regular" | "big";
}

const renderOption = (
  option: Record<string, React.ReactNode>,
  data: Record<string, unknown>
): React.ReactElement => (
  <>
    <StyledOptionWrapper>
      <StyledOptionFlag>{getCountryFlag(option.id)}</StyledOptionFlag>
      <StyledOptionName>{option.name}</StyledOptionName>
    </StyledOptionWrapper>
    <StyledOptionRowEnd>
      <StyledOptionCode>{option.additionalInfo}</StyledOptionCode>
      <RadioButton
        checked={data.checked as boolean}
        onChange={data.onChange as ChangeEventHandler<HTMLInputElement>}
      />
    </StyledOptionRowEnd>
  </>
);

export const InputPhone = forwardRef<HTMLInputElement, InputPhoneProps>(
  (
    {
      size = "regular",
      disabled,
      value: defaultValue = "",
      onChange,
      defaultCountry = "US",
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const handleRef = useForkRef(ref, inputRef);
    const [prefix, setPrefix] = useState(defaultCountry);
    const [value, setValue] = useState<string>("");

    const options = countriesArraySortedByName.map((country: Country) => ({
      id: country.countryCode,
      name: country.name,
      additionalInfo: `+${country.callingCode}`,
    }));

    const separatePrefixFromValue = (
      fullValue: string
    ): { newPrefix: string; newValue: string } => {
      let newPrefix = prefix;
      let newValue = fullValue;

      if (fullValue.startsWith("+") || fullValue.startsWith("00")) {
        const prefixLength = fullValue.startsWith("+") ? 1 : 2;
        const digits = fullValue.slice(prefixLength);

        for (let i = digits.length; i >= 1; i--) {
          const code = digits.slice(0, i);
          if (callingCodeToCountryCodeMap[code]) {
            newPrefix = callingCodeToCountryCodeMap[code];
            const dialCode = getCountryCallingCode(newPrefix);
            newValue = fullValue.slice(prefixLength + dialCode.length);
            break;
          }
        }
      }

      return { newPrefix, newValue };
    };

    useEffect(() => {
      const { newPrefix, newValue } = separatePrefixFromValue(defaultValue);
      setPrefix(newPrefix);
      setValue(newValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        if (/[^0-9+]/.test(inputValue)) {
          return;
        }

        const { newPrefix, newValue } = separatePrefixFromValue(inputValue);
        setPrefix(newPrefix);
        setValue(newValue);

        typeof onChange === "function" &&
          onChange(`+${getCountryCallingCode(newPrefix)}${newValue}`);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [onChange, prefix]
    );

    const handleSelectChange = (val: string | number | (string | number)[]) => {
      setPrefix(val as string);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
      typeof onChange === "function" &&
        onChange(`+${getCountryCallingCode(val)}${value}`);
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();

      const paste = e.clipboardData.getData("text");
      const filteredPaste = paste.replace(/[^0-9+]/g, "");

      const inputElement = e.currentTarget as HTMLInputElement;
      const { selectionStart, selectionEnd } = inputElement;

      if (selectionStart !== null && selectionEnd !== null) {
        const currentValue = inputElement.value;
        const beforeSelection = currentValue.slice(0, selectionStart);
        const afterSelection = currentValue.slice(selectionEnd);

        const newValueAfterPaste =
          beforeSelection + filteredPaste + afterSelection;
        const { newPrefix, newValue } =
          separatePrefixFromValue(newValueAfterPaste);

        setPrefix(newPrefix);
        setValue(newValue);

        typeof onChange === "function" &&
          onChange(`+${getCountryCallingCode(newPrefix)}${newValue}`);
      }
    };

    return (
      <Input
        {...rest}
        ref={handleRef}
        size={size}
        value={value}
        disabled={disabled}
        type="text"
        onPaste={handlePaste}
        onChange={handleChange}
        data-testid="InputPhone"
        startAdornment={
          <InputSelect
            size={size}
            onChange={handleSelectChange}
            options={options}
            selected={prefix}
            filterCriteria={(option, filter) => {
              return !!option?.additionalInfo?.toLowerCase().includes(filter);
            }}
            selectedLabel={
              <>
                <StyledFlagTriggerLabel $size={size}>
                  {getCountryFlag(prefix)}
                </StyledFlagTriggerLabel>
                +{getCountryCallingCode(prefix)}
              </>
            }
            disabled={disabled}
            keepSameOptionsOrder={false}
            search
            renderOption={renderOption}
          />
        }
      />
    );
  }
);

InputPhone.displayName = "InputPhone";
